import React from 'react';
import { SEO } from '../../components/seo/SEO';
import { Scale, Shield, Mail, Building2, FileText, AlertCircle } from 'lucide-react';
import { PageLayout } from '../../components/layout/PageLayout';
import { useTranslation } from '../../hooks/useTranslation';
import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';

export function LegalNotice() {
  const { t } = useTranslation();
  const { getPathForLang } = useI18n();
  
  return (
    <PageLayout disableAds={true}>
      <SEO
        title={t('legal_notice.seo.title')}
        description={t('legal_notice.seo.description')}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('legal_notice.title')}</h1>
            <p className="text-lg text-gray-600">
              {t('legal_notice.subtitle')}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-8">
              <div className="prose prose-blue max-w-none">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 mb-8">
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <AlertCircle className="w-6 h-6 text-blue-600" />
                    </div>
                    <h2 className="ml-4 text-2xl font-semibold text-gray-900">{t('legal_notice.important_info.title')}</h2>
                  </div>
                  <p className="text-gray-600">
                    {t('legal_notice.important_info.description')}
                  </p>
                </div>

                <div className="grid gap-8 mb-12">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <Building2 className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="ml-4 text-lg font-semibold text-gray-900">{t('legal_notice.publisher.title')}</h3>
                    </div>
                    <div className="space-y-3 text-gray-600">
                      <p>
                        {t('legal_notice.publisher.description1')}
                      </p>
                      <p>
                        {t('legal_notice.publisher.description2')}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <Mail className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="ml-4 text-lg font-semibold text-gray-900">{t('legal_notice.contact.title')}</h3>
                    </div>
                    <div className="space-y-3 text-gray-600">
                      <p>
                        {t('legal_notice.contact.description')}
                      </p>
                      <ul className="list-disc list-inside">
                        <li>{t('legal_notice.contact.email')}</li>
                        <li>{t('legal_notice.contact.form')} <Link to={getPathForLang("/contact")} className="text-blue-600 hover:text-blue-800">www.pokemonpocket.fr/contact</Link></li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <Shield className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="ml-4 text-lg font-semibold text-gray-900">{t('legal_notice.hosting.title')}</h3>
                    </div>
                    <div className="space-y-3 text-gray-600">
                      <p>
                        {t('legal_notice.hosting.description')}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <Scale className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="ml-4 text-lg font-semibold text-gray-900">{t('legal_notice.intellectual_property.title')}</h3>
                    </div>
                    <div className="space-y-3 text-gray-600">
                      <p>
                        {t('legal_notice.intellectual_property.description1')}
                      </p>
                      <p>
                        {t('legal_notice.intellectual_property.description2')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-xl p-6 mb-8 border-l-4 border-yellow-400">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <FileText className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('legal_notice.disclaimer.title')}</h3>
                      <p className="text-gray-600">
                        {t('legal_notice.disclaimer.description')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-gray-500 border-t border-gray-200 pt-6">
                  <p>
                    {t('legal_notice.last_update')} {new Date().toLocaleDateString(localStorage.getItem('i18nextLng') === 'en' ? 'en-US' : 'fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}